import { useState, useEffect } from "react";
import { X, ArrowRight, ArrowLeft } from "lucide-react";
import { Button } from "./Button";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { useCountryTranslations } from "../contexts/CountryTranslationsContext";
import { useMatches } from "../hooks/useMatches";
import { useModal } from "../contexts/ModalContext";
import { formatMatchTime } from "../lib/dateUtils";
import { trackFirstTimePrediction, trackFirstTimeSignUpClick } from "../lib/analytics";
import type { Match, Prediction } from "../types";

const PENDING_PREDICTION_KEY = "pending_first_prediction";
const FIRST_TIME_MODAL_SHOWN_KEY = "first_time_modal_shown";
const FIRST_TIME_CHOICE_KEY = "first_time_prediction_choice";

interface FirstTimePredictionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type PredictionChoice = "home" | "away" | "draw" | null;

interface StoredChoiceData {
  matchId: string;
  choice: PredictionChoice;
}

interface PendingPredictionData {
  matchId: string;
  prediction: Prediction;
}

export function FirstTimePredictionModal({
  isOpen,
  onClose,
}: FirstTimePredictionModalProps) {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const { getCountryName } = useCountryTranslations();
  const { matches, loading: matchesLoading } = useMatches();
  const { openLoginModal } = useModal();
  const [selectedChoice, setSelectedChoice] = useState<PredictionChoice>(null);
  const [showSignUpPrompt, setShowSignUpPrompt] = useState(false);

  // Get first upcoming match
  const firstMatch = matches.find((m) => m.status === "upcoming");

  // Restore previous choice when modal opens
  useEffect(() => {
    if (isOpen && firstMatch && !user) {
      // Check if there's a stored choice for this match
      const storedChoiceData = localStorage.getItem(FIRST_TIME_CHOICE_KEY);
      if (storedChoiceData) {
        try {
          const parsed: StoredChoiceData = JSON.parse(storedChoiceData);
          // Only restore if it's for the same match
          if (parsed.matchId === firstMatch.id && parsed.choice) {
            setSelectedChoice(parsed.choice);
            setShowSignUpPrompt(true);
          }
        } catch (error) {
          console.error("Failed to parse stored choice:", error);
          // Clear invalid data
          localStorage.removeItem(FIRST_TIME_CHOICE_KEY);
        }
      }
    } else if (!isOpen) {
      setSelectedChoice(null);
      setShowSignUpPrompt(false);
    }
  }, [isOpen, firstMatch, user]);

  if (!isOpen) return null;

  // Don't show if user is logged in or no match available
  if (user || !firstMatch || matchesLoading) {
    return null;
  }

  const translatedHomeTeam = getCountryName(firstMatch.homeTeam);
  const translatedAwayTeam = getCountryName(firstMatch.awayTeam);

  const handleChoice = (choice: PredictionChoice) => {
    if (!choice || !firstMatch) return;
    
    setSelectedChoice(choice);
    setShowSignUpPrompt(true);
    
    // Track analytics event
    trackFirstTimePrediction(
      firstMatch.id,
      firstMatch.homeTeam,
      firstMatch.awayTeam,
      choice
    );
    
    // Store the choice in localStorage (even if they don't sign up)
    const choiceData: StoredChoiceData = {
      matchId: firstMatch.id,
      choice,
    };
    localStorage.setItem(FIRST_TIME_CHOICE_KEY, JSON.stringify(choiceData));
  };

  const handleSignUp = () => {
    if (!selectedChoice || !firstMatch) return;

    // Track analytics event for sign up click
    trackFirstTimeSignUpClick(
      firstMatch.id,
      firstMatch.homeTeam,
      firstMatch.awayTeam
    );

    // Calculate scores based on choice
    const homeScore = selectedChoice === "home" ? 1 : selectedChoice === "away" ? 0 : 1;
    const awayScore = selectedChoice === "away" ? 1 : selectedChoice === "home" ? 0 : 1;

    // Store the prediction in localStorage
    const prediction: Prediction = {
      homeTeam: firstMatch.homeTeam,
      awayTeam: firstMatch.awayTeam,
      homeFlag: firstMatch.homeFlag,
      awayFlag: firstMatch.awayFlag,
      homeScore,
      awayScore,
      mood: "confident", // Default mood
    };

    // Store match ID and prediction
    const pendingData: PendingPredictionData = {
      matchId: firstMatch.id,
      prediction,
    };

    localStorage.setItem(PENDING_PREDICTION_KEY, JSON.stringify(pendingData));
    localStorage.setItem(FIRST_TIME_MODAL_SHOWN_KEY, "true");

    // Close this modal and open login modal
    onClose();
    openLoginModal();
  };

  const handleSkip = () => {
    // Don't set FIRST_TIME_MODAL_SHOWN_KEY - let them see the modal again when they come back
    // Don't clear the choice - keep it so they can see it when they come back
    onClose();
  };

  const handleChangePrediction = () => {
    setShowSignUpPrompt(false);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-br from-primary to-primary-dark p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-2xl font-bold">
              {showSignUpPrompt
                ? t("firstTime.signUpToSave")
                : t("firstTime.welcome")}
            </h2>
            {!showSignUpPrompt && (
              <button
                onClick={handleSkip}
                className="p-2 hover:bg-white/20 rounded-lg transition-smooth"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
          <p className="text-white/90 text-sm">
            {showSignUpPrompt
              ? t("firstTime.signUpPrompt")
              : t("firstTime.subtitle")}
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          {!showSignUpPrompt ? (
            <>
              {/* Match and Choices in One Row */}
              <div className="mb-6">
                {/* Match Display - Compact */}
                <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4 mb-4">
                  <div className="text-center">
                    <div className="text-5xl mb-2">{firstMatch.homeFlag}</div>
                    <div className="text-sm font-semibold text-slate-900 px-2 break-words">
                      {translatedHomeTeam}
                    </div>
                  </div>
                  <div className="px-3 py-1.5 bg-gradient-to-br from-primary-lighter to-primary-light rounded-lg text-primary font-semibold text-sm">
                    {t("match.vs")}
                  </div>
                  <div className="text-center">
                    <div className="text-5xl mb-2">{firstMatch.awayFlag}</div>
                    <div className="text-sm font-semibold text-slate-900 px-2 break-words">
                      {translatedAwayTeam}
                    </div>
                  </div>
                </div>
                <div className="text-center text-sm text-slate-500 mb-6">
                  {formatMatchTime(firstMatch.matchTime, language)}
                </div>

                {/* Prediction Choices - Horizontal */}
                <div className="flex gap-3">
                  <button
                    onClick={() => handleChoice("home")}
                    className={`flex-1 p-5 rounded-2xl border-2 transition-all flex flex-col items-center justify-center min-h-[120px] ${
                      selectedChoice === "home"
                        ? "border-primary bg-primary text-white shadow-lg scale-105"
                        : "border-slate-300 bg-white hover:border-primary hover:bg-slate-50"
                    }`}
                  >
                    <div className="text-5xl mb-2">{firstMatch.homeFlag}</div>
                    {selectedChoice === "home" && (
                      <div className="text-sm font-bold mt-1">✓</div>
                    )}
                  </button>

                  <button
                    onClick={() => handleChoice("draw")}
                    className={`flex-1 p-5 rounded-2xl border-2 transition-all flex flex-col items-center justify-center min-h-[120px] ${
                      selectedChoice === "draw"
                        ? "border-primary bg-primary text-white shadow-lg scale-105"
                        : "border-slate-300 bg-white hover:border-primary hover:bg-slate-50"
                    }`}
                  >
                    <div className="text-2xl font-bold text-slate-700 mb-2">
                      {selectedChoice === "draw" ? (
                        <span className="text-white">—</span>
                      ) : (
                        "—"
                      )}
                    </div>
                    {selectedChoice === "draw" && (
                      <div className="text-sm font-bold mt-1">✓</div>
                    )}
                  </button>

                  <button
                    onClick={() => handleChoice("away")}
                    className={`flex-1 p-5 rounded-2xl border-2 transition-all flex flex-col items-center justify-center min-h-[120px] ${
                      selectedChoice === "away"
                        ? "border-primary bg-primary text-white shadow-lg scale-105"
                        : "border-slate-300 bg-white hover:border-primary hover:bg-slate-50"
                    }`}
                  >
                    <div className="text-5xl mb-2">{firstMatch.awayFlag}</div>
                    {selectedChoice === "away" && (
                      <div className="text-sm font-bold mt-1">✓</div>
                    )}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Selected Prediction Display */}
              <div className="bg-gradient-to-br from-primary-lighter to-primary-light rounded-xl p-4 mb-6 border border-primary/20">
                <div className="text-center">
                  <div className="text-sm text-primary font-medium mb-3">
                    {t("firstTime.yourPrediction")}
                  </div>
                  {selectedChoice === "draw" ? (
                    <div className="flex items-center gap-3">
                      <div className="flex-1 text-center">
                        <div className="text-3xl mb-1">{firstMatch.homeFlag}</div>
                        <div className="text-xs font-semibold text-slate-900">
                          {translatedHomeTeam}
                        </div>
                      </div>
                      <div className="text-lg font-bold text-primary px-4 flex-shrink-0">
                        {t("firstTime.draw")}
                      </div>
                      <div className="flex-1 text-center">
                        <div className="text-3xl mb-1">{firstMatch.awayFlag}</div>
                        <div className="text-xs font-semibold text-slate-900">
                          {translatedAwayTeam}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
                      <div className={`text-center ${selectedChoice === "home" ? "bg-primary/20 rounded-xl p-3 border-2 border-primary" : ""}`}>
                        <div className={`text-3xl mb-1 ${selectedChoice === "home" ? "scale-110" : ""}`}>{firstMatch.homeFlag}</div>
                        <div className={`text-xs font-semibold ${selectedChoice === "home" ? "text-primary font-bold" : "text-slate-900"}`}>
                          {translatedHomeTeam}
                        </div>
                      </div>
                      <div className="text-lg font-bold text-primary px-4">
                        {t("match.vs")}
                      </div>
                      <div className={`text-center ${selectedChoice === "away" ? "bg-primary/20 rounded-xl p-3 border-2 border-primary" : ""}`}>
                        <div className={`text-3xl mb-1 ${selectedChoice === "away" ? "scale-110" : ""}`}>{firstMatch.awayFlag}</div>
                        <div className={`text-xs font-semibold ${selectedChoice === "away" ? "text-primary font-bold" : "text-slate-900"}`}>
                          {translatedAwayTeam}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            {showSignUpPrompt ? (
              <>
                <Button variant="primary" fullWidth onClick={handleSignUp}>
                  <div className="flex items-center justify-center gap-2">
                    <span>{t("firstTime.signUpNow")}</span>
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </Button>
                <Button 
                  variant="secondary" 
                  fullWidth 
                  onClick={handleChangePrediction}
                >
                  <div className="flex items-center justify-center gap-2">
                    <ArrowLeft className="w-4 h-4" />
                    <span>{t("common.change")}</span>
                  </div>
                </Button>
                <Button variant="secondary" fullWidth onClick={handleSkip}>
                  {t("common.maybeLater")}
                </Button>
              </>
            ) : (
              <Button
                variant="primary"
                fullWidth
                onClick={() => {
                  if (selectedChoice) {
                    handleChoice(selectedChoice);
                  }
                }}
                disabled={!selectedChoice}
              >
                {t("firstTime.continue")}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Export the localStorage key for use in other components
export { PENDING_PREDICTION_KEY, FIRST_TIME_MODAL_SHOWN_KEY, FIRST_TIME_CHOICE_KEY };

